import React, { useState, useEffect, useRef, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Context
import { CartProvider } from "./context/CartContext";

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Questionnaire from "./pages/Questionnaire";
import BrandResults from "./pages/BrandResults";
import BrandDetail from "./pages/BrandDetail";
import Store from "./pages/Store";
import StoreSuccess from "./pages/StoreSuccess";
import DomainRequest from "./pages/DomainRequest";
import ActivateBrand from "./pages/ActivateBrand";
import DomainClaim from "./pages/DomainClaim";
import DomainConnect from "./pages/DomainConnect";
import FAQ from "./pages/FAQ";
import GuidedCreationPage from "./pages/GuidedCreationPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Auth Context
export const AuthContext = React.createContext(null);

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          });
          
          const { user } = response.data;
          
          // Check for stored return URL (for cart context-aware redirects)
          const checkoutReturnUrl = sessionStorage.getItem('checkoutReturnUrl');
          sessionStorage.removeItem('checkoutReturnUrl'); // Clean up
          
          // Determine redirect destination based on context
          let redirectPath = "/dashboard"; // Default
          if (checkoutReturnUrl) {
            // Validate the return URL is a valid path (not external)
            if (checkoutReturnUrl.startsWith('/')) {
              redirectPath = checkoutReturnUrl;
            }
          }
          
          // Navigate with user data
          navigate(redirectPath, { state: { user }, replace: true });
        } catch (error) {
          console.error("Auth error:", error);
          toast.error("Authentication failed. Please try again.");
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
      <div className="text-center">
        <div className="spinner mx-auto mb-4"></div>
        <p className="text-[#78716C]">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component - checks if user is authenticated
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const { user, isLoading } = useContext(AuthContext);

  useEffect(() => {
    // Only redirect after loading is complete and user is not authenticated
    if (!isLoading && !user) {
      navigate("/", { replace: true });
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-[#78716C]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect via useEffect
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();
  
  // Check for session_id in URL fragment synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/guided-creation" element={<GuidedCreationPage />} />
      <Route path="/create" element={<Questionnaire />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/questionnaire" element={
        <ProtectedRoute>
          <Questionnaire />
        </ProtectedRoute>
      } />
      <Route path="/brand-results" element={
        <ProtectedRoute>
          <BrandResults />
        </ProtectedRoute>
      } />
      <Route path="/brand/:brandId" element={
        <ProtectedRoute>
          <BrandDetail />
        </ProtectedRoute>
      } />
      <Route path="/store" element={<Store />} />
      <Route path="/store/success" element={
        <ProtectedRoute>
          <StoreSuccess />
        </ProtectedRoute>
      } />
      <Route path="/domain-request" element={
        <ProtectedRoute>
          <DomainRequest />
        </ProtectedRoute>
      } />
      <Route path="/activate-brand/:brandId" element={
        <ProtectedRoute>
          <ActivateBrand />
        </ProtectedRoute>
      } />
      <Route path="/domain-claim/:purchaseId" element={
        <ProtectedRoute>
          <DomainClaim />
        </ProtectedRoute>
      } />
      <Route path="/domain-connect/:purchaseId" element={
        <ProtectedRoute>
          <DomainConnect />
        </ProtectedRoute>
      } />
    </Routes>
  );
};

// Global Auth Provider - wraps all routes to share authentication state
const GlobalAuthProvider = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for user from location state (passed from AuthCallback) or verify with backend
  useEffect(() => {
    const checkAuth = async () => {
      // First check if user data is in location state (just came from login)
      if (location.state?.user) {
        setUser(location.state.user);
        setIsLoading(false);
        return;
      }

      // Otherwise, verify with backend using session cookie
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        // User is not logged in - that's ok for public pages
        console.log("User not authenticated");
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []); // Only run once on mount

  // Also check when location state changes (after login redirect)
  useEffect(() => {
    if (location.state?.user && !user) {
      setUser(location.state.user);
    }
  }, [location.state?.user, user]);

  return (
    <AuthContext.Provider value={{ user, setUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <BrowserRouter>
        <GlobalAuthProvider>
          <CartProvider>
            <AppRouter />
          </CartProvider>
        </GlobalAuthProvider>
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            fontFamily: "'Manrope', sans-serif"
          }
        }}
      />
    </div>
  );
}

export default App;
